import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEdit, faXmark, faLock } from "@fortawesome/pro-solid-svg-icons";
import { Text, Tooltip, Flex, ActionIcon, Button } from "@mantine/core";
import type { ColumnDef } from "@tanstack/react-table";
import { createColumnHelper } from "@tanstack/table-core";

import {
  AssessmentQuestionResponseFormat,
  ReviewPageBasicQuestion,
  QuestionType,
  RESPONSE_FORMAT_LABELS,
  type ReviewPageCompetency,
} from "@/types/screener-builder";
import {
  competencyHasAssessmentQuestion,
  getIncludedAssessmentCompetencyCount,
  MIN_COMPETENCIES_AND_QUESTIONS,
  getMinimumRequiredDeleteTooltip,
} from "@/utils/screener-builder";
import { buildTestId } from "@/utils/helpers";

export const getColumnVisibilityState = (isReadOnly: boolean) => ({
  delete: !isReadOnly,
  details: isReadOnly,
  edit: !isReadOnly,
});

const QUESTION_TYPE_MAP: Record<QuestionType, string> = {
  [QuestionType.YesNo]: "Yes/No",
  [QuestionType.SingleChoice]: "Single Choice",
  [QuestionType.MultipleChoice]: "Multiple Choice",
  [QuestionType.Number]: "Number",
  [QuestionType.ShortAnswer]: "Short Answer",
  [QuestionType.Name]: "Name",
  [QuestionType.Email]: "Email",
  [QuestionType.Phone]: "Phone",
  [QuestionType.Voice]: "Voice",
  [QuestionType.FileUpload]: "File upload",
};
const isEditableQuestionType = (questionType?: QuestionType) => {
  return (
    questionType !== undefined &&
    questionType !== QuestionType.Name &&
    questionType !== QuestionType.Email &&
    questionType !== QuestionType.Phone
  );
};

export enum BasicQuestionColumnIds {
  Edit = "edit",
  Delete = "delete",
  Details = "details",
  Question = "question",
  Type = "type",
}

export const formatBasicQuestionColumns = <
  T extends QuestionType = QuestionType,
>(
  questions: ReviewPageBasicQuestion<T>[],
  handleEdit: (row: ReviewPageBasicQuestion<T>) => void,
  handleDelete: (id: string) => void,
  isRowLocked: (row: ReviewPageBasicQuestion<T>) => boolean,
  readOnly: boolean,
  track: (eventName: string, properties?: Record<string, unknown>) => void,
) => {
  const columnHelper = createColumnHelper<ReviewPageBasicQuestion<T>>();
  const hasType = questions.some((q) => q.questionType);

  const columns = [];
  columns.push(
    columnHelper.accessor("questionText", {
      id: BasicQuestionColumnIds.Question,
      header: "QUESTION",
      cell: (info) => {
        const questionText = info.getValue();
        return (
          <Text
            className="font-normal whitespace-normal break-words"
            size="sm"
            style={{ paddingRight: 0, marginRight: 0 }}
          >
            {questionText}
          </Text>
        );
      },
      meta: {
        style: { paddingRight: 0 },
      },
    }),
  );

  if (hasType) {
    columns.push(
      columnHelper.accessor("questionType", {
        id: BasicQuestionColumnIds.Type,
        header: "TYPE",
        cell: (info) => (
          <Text className="font-semibold" size="sm">
            {
              QUESTION_TYPE_MAP[
                info.getValue() as keyof typeof QUESTION_TYPE_MAP
              ]
            }
          </Text>
        ),
      }),
    );
  }

  columns.push(
    columnHelper.display({
      id: BasicQuestionColumnIds.Edit,
      header: () => null,
      cell: (info) => {
        if (!isEditableQuestionType(info.row.original.questionType)) {
          return null;
        }

        return (
          <>
            {isEditableQuestionType(info.row.original.questionType) && (
              <>
                <Flex
                  align="center"
                  className="cursor-pointer"
                  data-testid={buildTestId(
                    "builder-review",
                    "basic-questions",
                    "edit-question",
                    info.row.index,
                  )}
                  gap="none"
                  justify="flex-end"
                  onClick={() => {
                    track("Edit Question Button Clicked", {
                      question_id: info.row.original.internalLabel,
                      question_type: info.row.original.questionType,
                    });
                    handleEdit(info.row.original);
                  }}
                >
                  <Text className="font-semibold pt-1" size="xs" c="purple">
                    EDIT
                  </Text>
                  <ActionIcon variant="transparent" className="purple">
                    <FontAwesomeIcon icon={faEdit} />
                  </ActionIcon>
                </Flex>
              </>
            )}
          </>
        );
      },
    }),
  );

  columns.push(
    columnHelper.display({
      id: BasicQuestionColumnIds.Delete,
      header: () => null,
      cell: (info) => {
        const isEditableType = isEditableQuestionType(
          info.row.original.questionType,
        );
        const isLocked = isRowLocked(info.row.original) || !isEditableType;

        return (
          <Tooltip
            label={
              isEditableType
                ? getMinimumRequiredDeleteTooltip("basic question")
                : "This question is included in every screener and can't be edited"
            }
            content="Edit Question"
            disabled={!isLocked}
          >
            <Flex justify="flex-end" align="center">
              {!isEditableType ? (
                <FontAwesomeIcon
                  icon={faLock}
                  size="sm"
                  className="text-gray-400 mx-2"
                />
              ) : (
                <ActionIcon
                  className="disabled:bg-transparent text-purple"
                  data-testid={buildTestId(
                    "builder-review",
                    "basic-questions",
                    "delete-question",
                    info.row.index,
                  )}
                  disabled={isLocked}
                  onClick={() => {
                    track("Remove Question X Button Clicked", {
                      question_id: info.row.original.id,
                      question_type: info.row.original.questionType,
                    });
                    handleDelete(info.row.original.id || "");
                  }}
                  variant="transparent"
                >
                  <FontAwesomeIcon icon={faXmark} size="lg" />
                </ActionIcon>
              )}
            </Flex>
          </Tooltip>
        );
      },
    }),
  );

  columns.push(
    columnHelper.display({
      id: BasicQuestionColumnIds.Details,
      header: () => null,
      cell: (info) => {
        const isLocked =
          info.row.original.questionType === QuestionType.Name ||
          info.row.original.questionType === QuestionType.Email ||
          info.row.original.questionType === QuestionType.Phone;
        return (
          <Flex justify="flex-end" align="center">
            {isLocked ? (
              <FontAwesomeIcon
                icon={faLock}
                size="sm"
                className="text-gray-400 mx-2"
              />
            ) : (
              <Button
                className="px-0"
                data-testid={buildTestId(
                  "builder-review",
                  "basic-questions",
                  "details",
                  info.row.index,
                )}
                onClick={() => handleEdit(info.row.original)}
                variant="transparent"
              >
                Details
              </Button>
            )}
          </Flex>
        );
      },
    }),
  );

  return columns as ColumnDef<ReviewPageBasicQuestion, unknown>[];
};

export enum AssessmentQuestionColumnIds {
  Question = "question",
  Competency = "display_name",
  Format = "format",
  Edit = "edit",
  Delete = "delete",
  Details = "details",
}

export const formatAssessmentQuestionColumns = (
  competencies: ReviewPageCompetency[],
  handleEdit: (row: ReviewPageCompetency) => void,
  handleDelete: (id: string) => void,
  readOnly: boolean,
  track: (eventName: string, properties?: Record<string, unknown>) => void,
  {
    showFormatColumn = true,
    atMinimumAssessmentQuestions,
  }: {
    showFormatColumn?: boolean;
    atMinimumAssessmentQuestions?: boolean;
  } = {},
) => {
  const columnHelper = createColumnHelper<ReviewPageCompetency>();
  // When the review page splits voice/written into separate tables, each table
  // only sees its own subset, so the count must be computed from ALL
  // competencies and passed in (mirrors atMaxCompetencies). Fall back to the
  // local count when no override is provided (single-table case).
  const isAtMinimumIncludedCompetencies =
    atMinimumAssessmentQuestions ??
    getIncludedAssessmentCompetencyCount(competencies) <=
      MIN_COMPETENCIES_AND_QUESTIONS;

  const columns = [];

  columns.push(
    columnHelper.accessor("questions", {
      id: AssessmentQuestionColumnIds.Question,
      header: "QUESTION",
      cell: (info) => {
        const selectedQuestion = info.getValue().find((q) => q.isSelected);
        return (
          <Text
            className="font-normal whitespace-normal break-words"
            size="sm"
            style={{ paddingRight: 0, marginRight: 0 }}
          >
            {selectedQuestion?.question}
          </Text>
        );
      },
      size: 300,
      meta: {
        style: { paddingRight: 0 },
      },
    }),
  );

  columns.push(
    columnHelper.accessor("displayName", {
      id: AssessmentQuestionColumnIds.Competency,
      header: "COMPETENCY",
      cell: (info) => (
        <Text className="font-normal" size="sm" fw={500}>
          {info.getValue()}
        </Text>
      ),
    }),
  );

  if (showFormatColumn) {
    columns.push(
      columnHelper.display({
        id: AssessmentQuestionColumnIds.Format,
        header: "FORMAT",
        cell: (info) => {
          const selectedQuestion = info.row.original.questions.find(
            (q) => q.isSelected,
          );
          const format =
            selectedQuestion?.responseFormat ??
            AssessmentQuestionResponseFormat.Written;
          return (
            <Text className="font-normal" size="sm" fw={500}>
              {RESPONSE_FORMAT_LABELS[format]}
            </Text>
          );
        },
      }),
    );
  }

  columns.push(
    columnHelper.display({
      id: AssessmentQuestionColumnIds.Edit,
      header: () => null,
      size: 100,
      cell: (info) => {
        return (
          <Flex
            align="center"
            className="cursor-pointer"
            data-testid={buildTestId(
              "builder-review",
              "assessment-questions",
              "edit-question",
              info.row.index,
            )}
            gap="none"
            justify="flex-end"
            onClick={() => {
              track("Edit Competency Button Clicked", {
                competency_name: info.row.original.name,
                display_name: info.row.original.displayName,
              });
              handleEdit(info.row.original);
            }}
          >
            <Text className="font-semibold pt-1" size="xs" c="purple">
              EDIT
            </Text>
            <ActionIcon variant="transparent" className="purple">
              <FontAwesomeIcon icon={faEdit} />
            </ActionIcon>
          </Flex>
        );
      },
    }),
  );

  columns.push(
    columnHelper.display({
      id: AssessmentQuestionColumnIds.Delete,
      header: () => null,
      cell: (info) => {
        const competency = info.row.original;
        const disabled =
          competency.isIncluded &&
          isAtMinimumIncludedCompetencies &&
          competencyHasAssessmentQuestion(competency);

        return (
          <Tooltip
            label={
              disabled
                ? getMinimumRequiredDeleteTooltip("competency")
                : "Delete competency"
            }
          >
            <Flex justify="flex-end" align="center">
              <ActionIcon
                className="disabled:bg-transparent text-purple"
                disabled={disabled}
                data-testid={buildTestId(
                  "builder-review",
                  "assessment-questions",
                  "delete-question",
                  info.row.index,
                )}
                onClick={() => {
                  track("Remove Competency X Button Clicked", {
                    competency_name: info.row.original.name,
                    display_name: info.row.original.displayName,
                  });
                  handleDelete(info.row.original.id);
                }}
                variant="transparent"
              >
                <FontAwesomeIcon icon={faXmark} size="lg" />
              </ActionIcon>
            </Flex>
          </Tooltip>
        );
      },
      size: 1,
    }),
  );

  columns.push(
    columnHelper.display({
      id: AssessmentQuestionColumnIds.Details,
      header: () => null,
      cell: (info) => {
        return (
          <Flex justify="flex-end" align="center">
            <Button
              className="px-0"
              data-testid={buildTestId(
                "builder-review",
                "assessment-questions",
                "details",
                info.row.index,
              )}
              onClick={() => handleEdit(info.row.original)}
              variant="transparent"
            >
              Details
            </Button>
          </Flex>
        );
      },
    }),
  );

  return columns as ColumnDef<ReviewPageCompetency, unknown>[];
};

export const formatAlternativeCompetencyColumns = (
  handleEdit: (row: ReviewPageCompetency) => void,
) => {
  const columnHelper = createColumnHelper<ReviewPageCompetency>();
  return [
    columnHelper.accessor("name", {
      id: "name",
      header: "NAME",
      cell: (info) => (
        <Text className="font-normal" size="sm" fw={500}>
          {info.getValue()}
        </Text>
      ),
    }),
    columnHelper.display({
      id: "details",
      header: () => null,
      cell: (info) => {
        return (
          <Flex justify="flex-end" align="center">
            <Button
              className="px-0"
              data-testid={buildTestId(
                "builder-review",
                "legacy-assessment-questions",
                "details",
                info.row.index,
              )}
              onClick={() => handleEdit(info.row.original)}
              variant="transparent"
            >
              Details
            </Button>
          </Flex>
        );
      },
    }),
  ];
};

export const formatLegacyCompetencyColumns = () => {
  const columnHelper = createColumnHelper<ReviewPageBasicQuestion>();
  return [
    columnHelper.accessor("questionText", {
      id: "question",
      header: "QUESTION",
      cell: (info) => (
        <Text className="font-normal" size="sm" fw={400}>
          {info.getValue()}
        </Text>
      ),
    }),
  ];
};
